import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, getRetry } from "../lib/api";
import { useAuth } from "./AuthContext";

const StoreCtx = createContext(null);
export const useStore = () => useContext(StoreCtx);

const localGet = (k) => JSON.parse(localStorage.getItem(k) || "[]");

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(localGet("forge_cart"));
  const [bundleCart, setBundleCart] = useState(localGet("forge_bundle_cart"));
  const [wishlist, setWishlist] = useState(localGet("forge_wishlist"));
  const [currency, setCurrency] = useState(localStorage.getItem("forge_currency") || "USD");
  const [sections, setSections] = useState([]);
  const merged = useRef(false);

  const refreshSections = useCallback(() => {
    getRetry("/sections").then((r) => setSections(r.data)).catch((e) => console.warn("Sections load failed:", e?.message));
  }, []);
  useEffect(() => { refreshSections(); }, [refreshSections]);

  useEffect(() => {
    if (!user) { merged.current = false; return; }
    if (merged.current) return;
    merged.current = true;
    (async () => {
      try {
        const [c, w] = await Promise.all([api.get("/cart"), api.get("/wishlist")]);
        const mc = [...new Set([...c.data, ...localGet("forge_cart")])];
        const mw = [...new Set([...w.data, ...localGet("forge_wishlist")])];
        setCart(mc); setWishlist(mw);
        await Promise.all([api.put("/cart", { course_ids: mc }), api.put("/wishlist", { course_ids: mw })]);
      } catch (e) { console.warn("Cart/wishlist merge failed:", e?.message); }
    })();
  }, [user]);

  const persist = useCallback((key, ids) => {
    localStorage.setItem(key === "cart" ? "forge_cart" : "forge_wishlist", JSON.stringify(ids));
    if (user) api.put(`/${key}`, { course_ids: ids }).catch(() => {});
  }, [user]);

  const toggleCart = useCallback((id) => {
    setCart((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      persist("cart", next);
      return next;
    });
  }, [persist]);

  const toggleWishlist = useCallback((id) => {
    setWishlist((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      persist("wishlist", next);
      return next;
    });
  }, [persist]);

  const toggleBundle = useCallback((id) => {
    setBundleCart((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("forge_bundle_cart", JSON.stringify(next));
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart([]); persist("cart", []);
    setBundleCart([]); localStorage.setItem("forge_bundle_cart", "[]");
  }, [persist]);

  const changeCurrency = useCallback((c) => { setCurrency(c); localStorage.setItem("forge_currency", c); }, []);

  const value = useMemo(
    () => ({ cart, bundleCart, wishlist, currency, sections, refreshSections, toggleCart, toggleBundle, toggleWishlist, clearCart, changeCurrency }),
    [cart, bundleCart, wishlist, currency, sections, refreshSections, toggleCart, toggleBundle, toggleWishlist, clearCart, changeCurrency]
  );
  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}
